/*
 * Copyright © Siemens 2026
 */

import { inject } from "@angular/core";
import { CanActivateFn, Router, RouterStateSnapshot } from "@angular/router";
import { UserService, PublishingRole } from "../services/user.service";
import { AuthService } from "../services/auth.service";
import { JwtService } from "../services/jwt.service";
import { JwtVerificationUnavailableError } from "../services/jwt.service";

export const createUserGuard = (requiredRoles: PublishingRole[]): CanActivateFn => {
    return async ( _, state: RouterStateSnapshot) => {
        const userService = inject(UserService);
        const authService = inject(AuthService);
        const jwtService = inject(JwtService);
        const router = inject(Router);

        if(!authService.isAuthenticated) {
            authService.login();
            return false;
        }

        if(!userService.user()) {
            const token = authService.accessToken;
            if (!token) {
                authService.login();
                return false;
            }

            let payload: Awaited<ReturnType<JwtService['verifyToken']>>;
            try {
                payload = await jwtService.verifyToken(token);
            } catch (error) {
                // Keep the current route while the verification service is unavailable.
                if (error instanceof JwtVerificationUnavailableError) return false;
                throw error;
            }
            if (payload) {
                userService.setCurrentUser(payload);
            } else {
                authService.login();
                return false;
            }
        }
        if(requiredRoles.length && !userService.hasAnyRole(requiredRoles)) {
            return router.createUrlTree(
                ['/auth/unauthorized'],
                { queryParams: { returnUrl: state.url } }
            );
        }
        return true;
    };
}
